import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

import { getApiConfiguration } from '../../api'
import { DjRestAuthApi } from '../../api-client'
import { AuthenticationLoginByCodeApi } from '../../allauth-api-client'

const userClient = new DjRestAuthApi(getApiConfiguration(SERVER_URL_BASE))
const loginByCodeClient = new AuthenticationLoginByCodeApi(getApiConfiguration(SERVER_URL_BASE))

export const useUserStore = defineStore('user', () => {
    const user = ref(null);
    const isAuthenticated = computed(() => !!user.value);
    const loginUrl = SERVER_URL_BASE + '/accounts/login/code/';
    const loginFlowEmail = ref(null);
    const returnRoute = ref(null);
    const isInitialized = ref(false);

    async function init() {
        if (!isInitialized.value) {
            try {
                const success = await fetchUserDetails();
                // Only mark as initialized if we successfully got user data
                isInitialized.value = success;
                return success;
            } catch (error) {
                console.error('Failed to initialize user store:', error);
                // Only mark as initialized if it's not an auth error
                if (error.response?.status !== 401 && error.response?.status !== 403) {
                    isInitialized.value = true;
                }
                throw error;
            }
        }
        return isInitialized.value && !!user.value;
    }

    async function login(email, password) {
        try {
            await userClient.djRestAuthLoginCreate({ login: { email, password } });
            await fetchUserDetails();
            return true;
        } catch (error) {
            console.error('Login failed:', error);
            return false;
        }
    }

    async function logout() {
        try {
            await userClient.djRestAuthLogoutCreate();
            user.value = null;
            return true;
        } catch (error) {
            console.error('Logout failed:', error);
            throw error;
        }
    }

    async function register(email) {
        try {
            await userClient.djRestAuthRegistrationCreate({
                passwordlessRegistration: { email }
            });
            await fetchUserDetails();
            return true;
        } catch (error) {
            console.error('Registration failed:', error);
            return false;
        }
    }

    async function requestLoginCode(email) {
        try {
            await loginByCodeClient.allauthClientV1AuthCodeRequestPost({ client: 'browser', requestLoginCode: { email } });
            return true;
        } catch (error) {
            if (error.response?.status === 401) {
                return true;
            }
            console.error('Failed to request login code:', error);
            return false;
        }
    }

    async function confirmLoginCode(code) {
        try {
            const response = await loginByCodeClient.allauthClientV1AuthCodeConfirmPost({
                client: 'browser',
                confirmLoginCode: { code }
            });
            await fetchUserDetails();
            return true;
        } catch (error) {
            if (error.response?.status === 401) {
                return true;
            }
            else {
                console.error('Failed to confirm login code:', error);
                return false;
            }
        }
    }

    async function fetchUserDetails() {
        try {
            const response = await userClient.djRestAuthUserRetrieve();
            if (!response) {
                console.error('No response from user details endpoint');
                return false;
            }
            user.value = response;
            return true;
        } catch (error) {
            console.error('Failed to fetch user details:', error);
            // Only set user to null if we're not authenticated
            if (error.response?.status === 401 || error.response?.status === 403) {
                user.value = null;
            } else {
                throw error;
            }

        }
    }

    async function updateUserDetails(userDetails) {
        try {
            user.value = await userClient.djRestAuthUserUpdate({ customUser: userDetails });
            return true;
        } catch (error) {
            console.error('Failed to update user details:', error);
            return false;
        }
    }

    async function changePassword(oldPassword, newPassword) {
        try {
            await userClient.djRestAuthPasswordChangeCreate({
                passwordChange: {
                    new_password1: newPassword,
                    new_password2: newPassword,
                    old_password: oldPassword,
                }
            });
            return true;
        } catch (error) {
            console.error('Failed to change password:', error);
            return false;
        }
    }

    async function resetPassword(email) {
        try {
            await userClient.djRestAuthPasswordResetCreate({ passwordReset: { email } });
            return true;
        } catch (error) {
            console.error('Failed to reset password:', error);
            return false;
        }
    }

    async function verifyEmail(key) {
        try {
            await userClient.djRestAuthRegistrationVerifyEmailCreate({ verifyEmail: { key } });
            return true;
        } catch (error) {
            console.error('Failed to verify email:', error);
            return false;
        }
    }

    function getTeamByName(teamName) {
        return user.value?.teams?.find(team => team.name === teamName)
    }

    function getTeamBySlug(teamSlug) {
        return user.value?.teams?.find(team => team.slug === teamSlug)
    }

    function setLoginFlowEmail(email) {
        loginFlowEmail.value = email;
    }

    function clearLoginFlowEmail() {
        loginFlowEmail.value = null;
    }

    function setReturnRoute(route) {
        returnRoute.value = route;
    }

    function clearReturnRoute() {
        returnRoute.value = null;
    }

    return {
        user,
        isAuthenticated,
        loginUrl,
        login,
        logout,
        requestLoginCode,
        confirmLoginCode,
        register,
        fetchUserDetails,
        updateUserDetails,
        changePassword,
        resetPassword,
        verifyEmail,
        getTeamByName,
        getTeamBySlug,
        loginFlowEmail,
        setLoginFlowEmail,
        clearLoginFlowEmail,
        returnRoute,
        setReturnRoute,
        clearReturnRoute,
        init,
        isInitialized
    };
});